/**
 * ErectionPlans — CR-2026-ERECTOR-E1 (2026-10-13). AISC 207-25 §5.12.6.
 * One plan per job; every revision kept. Released revisions are locked
 * (server + database trigger TR-ER-001).
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, MenuItem, TextField,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  listErectionPlans, createErectionPlan, ERECTION_STATUS_LABEL, apiMessage, QF_ER_002_DOCX, QF_ER_002_PDF,
} from "../../utils/erectionPlansApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = { DRAFT: "default", PM_REVIEWED: "info", RELEASED: "success", SUPERSEDED: "default" };

export default function ErectionPlans() {
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions = [] } = loginUser?.decodedToken ?? {};
  const canEdit = hasPermission(userPermissions, PERMISSIONS.EDIT_ERECTION_PLAN);

  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ApiCall.get("/job")
      .then((res) => setJobs(res?.data?.data?.jobs || []))
      .catch((e) => setError(apiMessage(e, "Could not load jobs.")));
  }, []);

  useEffect(() => {
    setLoading(true);
    listErectionPlans(jobId || null)
      .then((p) => { setPlans(p); setError(null); })
      .catch((e) => setError(apiMessage(e, "Could not load erection plans.")))
      .finally(() => setLoading(false));
  }, [jobId]);

  const jobName = (id) => jobs.find((j) => Number(j.id) === Number(id))?.name || `Job #${id}`;
  const jobHasPlan = jobId && plans.some((p) => Number(p.job_id) === Number(jobId));

  async function create() {
    try {
      const plan = await createErectionPlan(jobId);
      navigate(`/erection-plans/${plan.id}`);
    } catch (e) { setError(apiMessage(e, "Plan not created.")); }
  }

  return (
    <>
      <Helmet><title>Erection Plans | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Erection Plans</Typography>
          {canEdit && (
            <Button variant="contained" startIcon={<AddRounded />} disabled={!jobId || jobHasPlan} onClick={create}>
              New plan for job
            </Button>
          )}
          <Button size="small" variant="outlined" href={QF_ER_002_DOCX} download>Blank form QF-ER-002 (Word)</Button>
          <Button size="small" href={QF_ER_002_PDF} target="_blank" rel="noopener">PDF</Button>
        </Stack>
        <PageGuide
          why="Every erection job needs a written plan covering the 21 items of AISC 207-25 §5.12.6, reviewed by the PM team before erection starts."
          who="PM team reviews; the site superintendent releases each revision; crews receive it."
          what="One plan per job. Each revision is kept; a released revision cannot be edited."
          how="Fill all 21 items (or mark N/A with a reason), record the §7.10 information, PM review, release, then record who it was communicated to."
          when="Before erection starts, and whenever the plan changes."
          standard="QF-ER-002 Rev 0 · OSHA 1926.752(e) · AISC 207-25 §5.12.6 · AISC 303 §7.10"
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select size="small" label="Job" value={jobId} onChange={(e) => setJobId(e.target.value)} sx={{ minWidth: 320 }}>
            <MenuItem value="">All jobs</MenuItem>
            {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
          </TextField>
        </Paper>
        <TableContainer component={Paper}>
          <SmartTable tableId="erection-plans" lockedColumns={["Job", "Revision", "Status"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell><TableCell>Revision</TableCell><TableCell>Status</TableCell>
                <TableCell>Items addressed</TableCell><TableCell>§7.10 info</TableCell><TableCell>Released</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && plans.length === 0 && (
                <TableRow><TableCell colSpan={6}>No erection plans yet{jobId ? " for this job" : ""}.</TableCell></TableRow>
              )}
              {plans.map((p) => (
                <TableRow key={p.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/erection-plans/${p.id}`)}>
                  <TableCell>{jobName(p.job_id)}</TableCell>
                  <TableCell>Rev {p.revision}</TableCell>
                  <TableCell><Chip size="small" color={STATUS_COLOR[p.status]} label={ERECTION_STATUS_LABEL[p.status] || p.status} /></TableCell>
                  <TableCell>{21 - (p.unaddressed?.length ?? 21)} of 21</TableCell>
                  <TableCell>{p.cosp_7_10_provided ? "Recorded" : "Missing"}</TableCell>
                  <TableCell>{p.released_at ? new Date(p.released_at).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
